import { useCallback, useEffect, useRef, useState } from "react";
import { logger } from "../lib/logger";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle, Bookmark, Command, FileText, Loader2, Plus, Share2, Sparkles, Type, Video, Wand2, X, Zap,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AppShell } from "../components/AppShell";
import { CopyBtn } from "../components/CopyBtn";
import { ShareDropdown } from "../components/ShareDropdown";
import { RefineModal } from "../components/RefineModal";
import { PublishModal } from "../components/PublishModal";
import { MdOut } from "../components/MdOut";
import { api, formatError } from "../lib/api";
import { tplIcons } from "../components/icons";

const TONES = ["professional", "casual", "witty", "persuasive", "formal", "friendly", "urgent"];

export default function GeneratePage() {
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("medium");
  const [provider, setProvider] = useState("openai");
  const [ctx, setCtx] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showRefine, setShowRefine] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [videoLoading, setVideoLoading] = useState(false);
  const [cloneActive, setCloneActive] = useState(false);
  const [rating, setRating] = useState(0); // 1, -1, or 0
  const formRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [t, p, c] = await Promise.all([api.get("/templates"), api.get("/prompts"), api.get("/clone").catch(() => ({ data: null }))]);
        setTemplates(t.data);
        setPrompts(p.data);
        if (t.data.length) setSelected(t.data[0].id);
        if (c?.data?.active) setCloneActive(true);
      } catch (error) {
        logger.error("Failed to load generate page data", error);
      }
      // Prefill topic from Trend Radar / Daily Ideas
      const preset = sessionStorage.getItem("cf_prefill_topic");
      if (preset) {
        setTopic(preset);
        sessionStorage.removeItem("cf_prefill_topic");
      }
    })();
  }, []);

  const submitForm = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && !loading && topic.trim()) {
        e.preventDefault();
        submitForm();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [loading, topic, submitForm]);

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setErr("");
    setResult(null);
    setLoading(true);
    try {
      const { data } = await api.post("/generate", { template: selected, topic, tone, additional_context: ctx, length, provider });
      setResult(data);
      toast.success(`Generated! ${data.word_count} words`);
      await refreshUser();
    } catch (e) {
      logger.error("Generate error", e);
      const msg = formatError(e.response?.data?.detail);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onRefined = async (d) => {
    setResult((prev) => ({ ...prev, output: d.output, word_count: d.word_count }));
    await refreshUser();
  };

  const savePrompt = async () => {
    if (!saveName.trim() || !topic.trim()) return;
    try {
      await api.post("/prompts", { name: saveName, template: selected, topic, tone, additional_context: ctx, length });
      const { data } = await api.get("/prompts");
      setPrompts(data);
      setShowSave(false);
      setSaveName("");
      toast.success("Prompt saved!");
    } catch (error) {
      logger.error("Save prompt error", error);
      toast.error("Failed to save");
    }
  };

  const loadPrompt = (p) => {
    setSelected(p.template);
    setTopic(p.topic);
    setTone(p.tone || "professional");
    setCtx(p.additional_context || "");
    setLength(p.length || "medium");
    toast.success(`Loaded: ${p.name}`);
  };

  const delPrompt = async (id) => {
    try {
      await api.delete(`/prompts/${id}`);
      setPrompts((ps) => ps.filter((p) => p.id !== id));
      toast.success("Deleted");
    } catch (error) {
      logger.error("Delete prompt error", error);
      toast.error("Failed to delete prompt");
    }
  };

  const generateVideoFromScript = async () => {
    setVideoLoading(true);
    try {
      const { data: job } = await api.post("/video/from-script", { generation_id: result.id, duration: 4, orientation: "portrait" });
      toast.success("Video queued! You'll see live progress on the Video page.");
      await refreshUser();
      // Pass the in-flight job_id so /video can pick up polling
      nav(`/video?job_id=${encodeURIComponent(job.job_id)}&prompt=${encodeURIComponent(job.prompt || "")}`);
    } catch (e) {
      logger.error("Script-to-video error", e);
      toast.error(formatError(e.response?.data?.detail) || "Failed");
    } finally {
      setVideoLoading(false);
    }
  };

  const rateOutput = async (val) => {
    if (!result?.id) return;
    setRating(val);
    try {
      await api.post("/clone/feedback", { generation_id: result.id, rating: val });
      toast.success(val === 1 ? "Saved 👍 — your Clone learns from this." : "Saved 👎 — your Clone will improve.");
    } catch (e) {
      logger.error("Rate error", e);
    }
  };


  const tpl = templates.find((t) => t.id === selected);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid="generate-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-black tracking-tighter text-zinc-950 dark:text-white font-heading">Generate Content</h1>
          {cloneActive && (
            <div className="flex items-center gap-2 bg-brand/10 text-brand px-3 py-1.5 text-xs font-bold tracking-wider uppercase" data-testid="clone-active-badge">
              <Sparkles className="w-3.5 h-3.5" /> Writing as you
            </div>
          )}
        </div>

        {prompts.length > 0 && (
          <div className="mb-6 flex items-center gap-2 overflow-x-auto pb-2" data-testid="saved-prompts-bar">
            <Bookmark className="w-4 h-4 text-zinc-400 flex-shrink-0" />
            <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider flex-shrink-0 mr-1">Quick:</span>
            {prompts.map((p) => (
              <div key={p.id} className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => loadPrompt(p)} className="text-xs font-semibold px-3 py-1.5 border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 hover:border-brand hover:text-brand transition-all" data-testid={`load-prompt-${p.id}`}>{p.name}</button>
                <button onClick={() => delPrompt(p.id)} className="text-zinc-300 hover:text-red-500 transition-colors"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <form ref={formRef} onSubmit={onSubmit} className="space-y-5">
              <div>
                <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-2">Template</label>
                <div className="space-y-2">
                  {templates.map((t) => {
                    const Icon = tplIcons[t.id] || FileText;
                    const isSelected = selected === t.id;
                    return (
                      <button
                        type="button"
                        key={t.id}
                        data-testid={`template-${t.id}`}
                        onClick={() => setSelected(t.id)}
                        className={`w-full flex items-center gap-3 p-3 border text-left transition-all ${isSelected ? "border-brand bg-brand/5 shadow-sm" : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800"}`}
                      >
                        <div className={`w-9 h-9 flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-brand text-white" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-400"}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-zinc-900 dark:text-white">{t.name}</p>
                          <p className="text-xs text-zinc-500">{t.credits} cr</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Topic</label>
                  <span className="text-xs text-zinc-400" data-testid="char-counter">{topic.length}/200</span>
                </div>
                <input data-testid="topic-input" type="text" value={topic} onChange={(e) => setTopic(e.target.value.slice(0, 200))} required maxLength={200} className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-zinc-900 dark:text-white focus:border-brand focus:ring-1 focus:ring-brand outline-none" placeholder="e.g., Benefits of remote work" />
              </div>

              <div>
                <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-2">AI Model</label>
                <div className="grid grid-cols-2 gap-2" data-testid="provider-selector">
                  {[
                    { id: "openai", name: "OpenAI", sub: "GPT-4o" },
                    { id: "anthropic", name: "Claude", sub: "Sonnet 4.5" },
                  ].map((p) => {
                    const active = provider === p.id;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        data-testid={`provider-${p.id}`}
                        onClick={() => setProvider(p.id)}
                        className={`p-3 border text-left transition-all ${active ? "border-brand bg-brand/5" : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800"}`}
                      >
                        <p className="font-semibold text-sm text-zinc-900 dark:text-white">{p.name}</p>
                        <p className="text-xs text-zinc-500">{p.sub}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">Tone</label>
                  <select data-testid="tone-select" value={tone} onChange={(e) => setTone(e.target.value)} className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-zinc-900 dark:text-white focus:border-brand focus:ring-1 focus:ring-brand outline-none appearance-none text-sm">
                    {TONES.map((t) => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">Length</label>
                  <select data-testid="length-select" value={length} onChange={(e) => setLength(e.target.value)} className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-zinc-900 dark:text-white focus:border-brand focus:ring-1 focus:ring-brand outline-none appearance-none text-sm">
                    <option value="short">Short (~150w)</option>
                    <option value="medium">Medium (~400w)</option>
                    <option value="long">Long (~700w)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">
                  Context <span className="text-zinc-400">(optional)</span>
                </label>
                <textarea data-testid="context-textarea" value={ctx} onChange={(e) => setCtx(e.target.value)} rows={2} className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-zinc-900 dark:text-white focus:border-brand focus:ring-1 focus:ring-brand outline-none resize-none text-sm" placeholder="Keywords, audience..." />
              </div>

              {err && (
                <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm p-3 flex items-center gap-2" data-testid="generate-error">
                  <AlertCircle className="w-4 h-4" />
                  {err}
                </div>
              )}

              <div className="flex gap-2">
                <button data-testid="generate-submit-btn" type="submit" disabled={loading} className="flex-1 bg-brand text-white font-semibold py-3.5 hover:bg-brand-hover flex items-center justify-center gap-2 disabled:opacity-50">
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-5 h-5" /> Generate ({tpl?.credits ?? 1} cr)</>
                  )}
                </button>
                {topic.trim() && !showSave && (
                  <button type="button" onClick={() => setShowSave(true)} className="px-3 py-3.5 border border-zinc-200 dark:border-zinc-700 text-zinc-500 hover:text-brand hover:border-brand transition-all" title="Save prompt" data-testid="save-prompt-btn">
                    <Bookmark className="w-5 h-5" />
                  </button>
                )}
              </div>
              {showSave && (
                <div className="flex gap-2 animate-slide-in" data-testid="save-prompt-form">
                  <input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Prompt name..." className="flex-1 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-sm text-zinc-900 dark:text-white outline-none focus:border-brand" data-testid="save-prompt-name" />
                  <button type="button" onClick={savePrompt} className="bg-brand text-white px-3 py-2 text-sm font-semibold" data-testid="confirm-save-prompt"><Plus className="w-4 h-4" /></button>
                  <button type="button" onClick={() => setShowSave(false)} className="text-zinc-400 px-2"><X className="w-4 h-4" /></button>
                </div>
              )}
              <p className="text-xs text-zinc-400 text-center flex items-center justify-center gap-1"><Command className="w-3 h-3" /> + Enter to generate</p>
            </form>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 min-h-[500px] flex flex-col sticky top-20" data-testid="output-panel">
              <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 px-4 py-3">
                <span className="text-sm font-medium text-zinc-500">Output</span>
                {result && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-zinc-400 flex items-center gap-1"><Type className="w-3 h-3" /> {result.word_count} words</span>
                    {cloneActive && (
                      <div className="flex items-center gap-1 border-l border-zinc-200 dark:border-zinc-700 pl-3" data-testid="clone-feedback-bar">
                        <button
                          onClick={() => rateOutput(1)}
                          data-testid="thumbs-up-btn"
                          className={`text-sm transition-colors ${rating === 1 ? "text-brand" : "text-zinc-400 hover:text-brand"}`}
                          title="Sounds like me"
                        >
                          👍
                        </button>
                        <button
                          onClick={() => rateOutput(-1)}
                          data-testid="thumbs-down-btn"
                          className={`text-sm transition-colors ${rating === -1 ? "text-red-500" : "text-zinc-400 hover:text-red-500"}`}
                          title="Doesn't sound like me"
                        >
                          👎
                        </button>
                      </div>
                    )}
                    <button onClick={() => setShowRefine(true)} className="flex items-center gap-1.5 text-sm text-brand font-medium hover:underline" data-testid="refine-btn">
                      <Wand2 className="w-3.5 h-3.5" /> Refine
                    </button>
                    <button onClick={() => setShowPublish(true)} className="flex items-center gap-1.5 text-sm text-pink-600 font-medium hover:underline" data-testid="publish-btn">
                      <Share2 className="w-3.5 h-3.5" /> Publish
                    </button>
                    <CopyBtn text={result.output} label="Copy" />
                    <ShareDropdown text={result.output} title={topic} />
                  </div>
                )}
              </div>
              <div className="flex-1 p-5 overflow-auto max-h-[600px]">
                {loading && (
                  <div className="flex flex-col items-center justify-center h-full gap-3" data-testid="generating-loader">
                    <div className="relative">
                      <Loader2 className="w-10 h-10 animate-spin text-brand" />
                      <Sparkles className="w-4 h-4 text-brand absolute -top-1 -right-1 animate-pulse" />
                    </div>
                    <p className="text-sm text-zinc-500 font-medium">Writing {length} content...</p>
                  </div>
                )}
                {!loading && result && (
                  <div data-testid="generated-output" className="animate-fade-in">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-xs font-bold tracking-[0.15em] text-brand uppercase bg-brand/5 px-2 py-1">{result.template_name}</span>
                    </div>
                    <MdOut content={result.output} />
                    {tpl?.category === "video" && (
                      <div className="mt-6 border-t border-zinc-200 dark:border-zinc-800 pt-4" data-testid="script-to-video-section">
                        <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-950/30 dark:to-purple-950/30 border border-pink-200 dark:border-pink-800 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Video className="w-5 h-5 text-pink-500" />
                            <span className="text-sm font-bold text-zinc-900 dark:text-white">Turn this script into a video?</span>
                          </div>
                          <p className="text-xs text-zinc-500 mb-3">AI will convert your script into a cinematic Sora 2 video. Takes 2-5 min.</p>
                          <button onClick={generateVideoFromScript} disabled={videoLoading} data-testid="script-to-video-btn" className="bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold px-5 py-2.5 text-sm flex items-center gap-2 hover:opacity-90 transition-all disabled:opacity-50">
                            {videoLoading ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /> Generating Video...</>
                            ) : (
                              <><Video className="w-4 h-4" /> Make Video (5 cr)</>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {!loading && !result && (
                  <div className="flex flex-col items-center justify-center h-full text-zinc-300" data-testid="empty-output">
                    <div className="w-16 h-16 bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                      <Sparkles className="w-8 h-8 text-zinc-300 dark:text-zinc-600" />
                    </div>
                    <p className="text-sm font-medium text-zinc-400">Your content appears here.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showRefine && result && <RefineModal genId={result.id} onClose={() => setShowRefine(false)} onRefined={onRefined} />}
      {showPublish && result && <PublishModal content={result.output} topic={topic} onClose={() => setShowPublish(false)} />}
    </AppShell>
  );
}

// Keep Zap import used in case it's referenced
void Zap;
